import React from "react";
import { Table, Button } from "react-bootstrap";
import { PDFDocument, rgb, StandardFonts } from "pdf-lib";
import { saveAs } from "file-saver";
import ToPeso from "../components/misc/ToPeso";
import { format, addMonths, addWeeks, addDays, addYears } from "date-fns";


function drawFormattedText(text, x, y, maxWidth, lineHeight, defaultFont, boldFont, fontSize, page, color) {
  const boldRegex = /<b>(.*?)<\/b>/g; // Regex to detect bold tags
  const stripTags = (text) => text.replace(/<b>|<\/b>/g, ""); // Function to remove tags

  // Split text by newlines first
  const paragraphs = text.split("\n");

  let currentY = y;

  for (const paragraph of paragraphs) {
    const words = paragraph.split(" "); // Split paragraph into words
    let currentLine = ""; // Current line being built
    let currentLine2 = ""; // Current line being built
    let lines = []; // Array to store formatted lines

    for (const word of words) {
      const strippedWord = stripTags(word); // Remove tags for drawing
      const testFont = word.match(boldRegex) ? boldFont : defaultFont;
      const testLine = currentLine + (currentLine ? " " : "") + strippedWord;
      const testLine2 = currentLine2 + (currentLine2 ? " " : "") + word;
      const textWidth = testFont.widthOfTextAtSize(testLine, fontSize);

      if (textWidth > maxWidth) {
        lines.push(currentLine2); // Save the current line
        currentLine = strippedWord; // Start a new line
        currentLine2 = word; // Start a new line
      } else {
        currentLine = testLine; // Add word to the current line
        currentLine2 = testLine2; // Start a new line
      }
    }

    // Push the last line
    if (currentLine) {
      lines.push(currentLine2);
    }

    // Draw each line with formatting
    for (const line of lines) {
      let offsetX = x; // Start x-coordinate
      const formattedWords = line.split(" "); // Split into words for formatting

      for (const word of formattedWords) {
        const isBold = word.match(boldRegex); // Check if word is bold
        const strippedWord = stripTags(word); // Remove tags for drawing
        const wordFont = isBold ? boldFont : defaultFont; // Apply bold font if detected

        // Draw the word
        page.drawText(strippedWord, {
          x: offsetX,
          y: currentY,
          size: fontSize,
          font: wordFont, // Correct font applied
          color: color,
        });

        // Adjust x-position for the next word
        offsetX += wordFont.widthOfTextAtSize(strippedWord + " ", fontSize);
      }

      currentY -= lineHeight; // Move down for the next line
    }

    currentY -= lineHeight; // Additional spacing between paragraphs
  }
}

const numberToWords = (num) => {
  // Make sure num is a valid number
  const validNum = parseFloat(num);
  
  if (isNaN(validNum)) {
    return "<b>Zero</b>";
  }

  if (validNum === 0) return "<b>Zero</b>";

  const belowTwenty = [
    "Zero", "One", "Two", "Three", "Four", "Five", "Six", "Seven", "Eight", "Nine",
    "Ten", "Eleven", "Twelve", "Thirteen", "Fourteen", "Fifteen", "Sixteen",
    "Seventeen", "Eighteen", "Nineteen"
  ];
  const tens = [
    "", "", "Twenty", "Thirty", "Forty", "Fifty", "Sixty", "Seventy", "Eighty", "Ninety"
  ];
  const thousands = ["", "Thousand", "Million", "Billion"];

  let word = "";

  const convertChunk = (n) => {
    let chunk = "";
    if (n >= 100) {
      chunk += belowTwenty[Math.floor(n / 100)] + " Hundred ";
      n %= 100;
    }
    if (n >= 20) {
      chunk += tens[Math.floor(n / 10)] + " ";
      n %= 10;
    }
    if (n > 0) {
      chunk += belowTwenty[n] + " ";
    }
    return chunk.trim();
  };

  // Separate integer and decimal parts
  const [integerPart, decimalPart] = validNum.toString().split(".");

  let i = 0;
  let intNum = parseInt(integerPart, 10);

  // Convert integer part
  while (intNum > 0) {
    const chunk = intNum % 1000;
    if (chunk > 0) {
      word = convertChunk(chunk) + (thousands[i] ? " " + thousands[i] : "") + " " + word;
    }
    intNum = Math.floor(intNum / 1000);
    i++;
  }

  word = word.trim();

  // Handle decimal part
  if (decimalPart) {
    const decimalNumber = parseInt(decimalPart.padEnd(2, '0').slice(0, 2), 10); // Ensure two digits for cents
    const decimalWords = decimalNumber > 0
      ? convertChunk(decimalNumber)
      : "Zero";
    word += ` and ${decimalWords} Cents`;
  }

  // Wrap each word with <b></b>
  return word
    .split(" ")
    .map((w) => `<b>${w}</b>`)
    .join(" ");
};


// Utility function for rounding up
const roundUp = (amount) => {
  const amountString = amount.toFixed(2);
  const secondDecimalPlace = amountString.charAt(amountString.length - 1);

  if (secondDecimalPlace !== "0") {
    return parseFloat((amount + 0.01).toFixed(2));
  }

  return amount;
};

// Function to calculate due dates and amounts
const calculateDueDates = (startDate, interval, term, amount, interest, adv) => {
  const dueDates = [];
  // Convert potential string inputs to numbers
  const numAmount = parseFloat(amount);
  const numInterest = parseFloat(interest);
  const numTerm = parseInt(term, 10);
  
  const principalAmount = numAmount / numTerm;
  const interestAmountInitial = (adv) ? (numAmount * numInterest) / 100 : 0;
  const interestAmountMinusAdvance = (((numAmount * numInterest) / 100) * numTerm) - interestAmountInitial;
  const interestAmount = interestAmountMinusAdvance / numTerm;

  let currentDate = new Date(startDate);

  for (let i = 0; i < numTerm; i++) {
    switch (interval.toLowerCase()) {
      case "monthly":
        currentDate = addDays(currentDate, 31);
        break;
      case "bimonthly": // Twice a month, roughly every two weeks
        currentDate = addWeeks(currentDate, 2);
        break;
      case "weekly":
        currentDate = addWeeks(currentDate, 1);
        break;
      case "quarterly":
        currentDate = addMonths(currentDate, 3);
        break;
      case "semiannual":
        currentDate = addMonths(currentDate, 6);
        break;
      case "yearly":
        currentDate = addYears(currentDate, 1);
        break;
      default:
        throw new Error("Invalid interval");
    }

    dueDates.push({
      amortizationNumber: i + 1,
      amortizationDate: format(currentDate, "MM/dd/yyyy"),
      principal: roundUp(principalAmount),
      interest: roundUp(interestAmount),
      total: roundUp(principalAmount + interestAmount),
    });
  }

  return dueDates;
};




// React Component
function DocumentPromissoryNote({ applicantDetails, loanProcessingDetails }) {
  if (!applicantDetails) {
    return null;
  }
  
  const generatePDF = async (loan) => {
    // Create schedule
    const schedule = calculateDueDates(
      loan.dateGranted,
      loan.loanInterval,
      loan.loanTerm,
      parseFloat(loan.amountGranted),  // Ensure this is a number
      parseFloat(loan.interestRate),   // Ensure this is a number
      loan.advanceInterest
    );

    console.log(schedule);
    
    const existingPdfBytes = await fetch("/pdftemplates/promissory-note.pdf").then(res =>
        res.arrayBuffer()
    );

    // Load PDF with pdf-lib
    const pdfDoc = await PDFDocument.load(existingPdfBytes);

    // Embed fonts
    const helveticaFont = await pdfDoc.embedFont(StandardFonts.Helvetica);
    const helveticaFontBold = await pdfDoc.embedFont(StandardFonts.HelveticaBold);

    // Get the first page
    const pages = pdfDoc.getPages();
    const firstPage = pages[0];

    // principal
    firstPage.drawText(parseFloat(loan.amountGranted).toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ','), {
      x: 116,
      y: 658,
      size: 9,
      font: helveticaFontBold, 
      color: rgb(0, 0, 0)
    });

    // date granted
    firstPage.drawText(format(new Date(loan.dateGranted), "MM/dd/yyyy"), {
      x: 116,
      y: 643,
      size: 9,
      font: helveticaFontBold, 
      color: rgb(0, 0, 0)
    });

    // loan number
    firstPage.drawText(loan.id.toString().padStart(6, '0'), {
      x: 385,
      y: 657,
      size: 9,
      font: helveticaFontBold, 
      color: rgb(0, 0, 0)
    });

    // loan product
    firstPage.drawText(loan.loanType.toUpperCase(), {
      x: 385,
      y: 642,
      size: 9,
      font: helveticaFontBold, 
      color: rgb(0, 0, 0)
    });

  // borrower name
  firstPage.drawText(`${applicantDetails.firstName.toUpperCase()} ${applicantDetails.middleName.toUpperCase() || ""} ${applicantDetails.lastName.toUpperCase()}`, {
    x: 94,
    y: 298,
    size: 9,
    font: helveticaFontBold, 
    color: rgb(0, 0, 0)
  });

  // address 1
  firstPage.drawText(`${applicantDetails.street.toUpperCase()}, ${applicantDetails.barangay.toUpperCase()},`, {
    x: 94,
    y: 286,
    size: 9,
    font: helveticaFontBold, 
    color: rgb(0, 0, 0)
  });

  // address 2
  firstPage.drawText(`${applicantDetails.municipality.toUpperCase()}, ${applicantDetails.province.toUpperCase()} ${applicantDetails.zipCode.toUpperCase()}`, {
    x: 94,
    y: 274,
    size: 9,
    font: helveticaFontBold, 
    color: rgb(0, 0, 0)
  });

  // account officer
  firstPage.drawText(loan.accountOfficer.toUpperCase(), {
    x: 372,
    y: 191,
    size: 9,
    font: helveticaFontBold, 
    color: rgb(0, 0, 0)
  });

    const companyName = '<b>HAYAHAY</b>  <b>LENDING</b> <b>CORP</b>';
    const principalAmount = parseFloat(loan.amountGranted);
    const interestRate = parseFloat(loan.interestRate) / 100;
    const term = parseFloat(loan.loanTerm);
    const totalAmount = principalAmount + (principalAmount * interestRate * term);

    const longText = `
    FOR VALUE RECEIVED, I/ WE jointly and severally promise to pay to order of ${companyName} with office in 2F, LCT Bidg., Ochoa Ave., Cor Lopez Jaena St. Brgy Tandang Sora Butuan City, the principal sum of PESOS ${numberToWords(totalAmount)} <b>Pesos</b> <b>Only</b> <b>(P${totalAmount.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',')})</b>, within a period of <b>${loan.loanTerm}</b> months payable in installment of PESOS ${numberToWords(parseFloat(schedule[0].total))} <b>Pesos</b> <b>Only</b> <b>(P${parseFloat(schedule[0].total).toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',')})</b>, the first installment to fall due on <b>${schedule[0].amortizationDate}</b> and ${loan.loanInterval} thereafter until the whole sum of principal shall have been fully paid. It is understood that the interest on this is <b>${loan.interestRate}%/month</b> for the said term/ period. 
    Failure or default to pay any installment when due shall render the entire amount outstanding under this note, without need for any notice, demand or presentment immediately due and demandable at the option of the holder. In such case, I/WE, jointly and severally agree to pay an additional amount equivalent to SIX (6) % per day of the amount due and demandable as penalty charges until fully paid. The ${companyName}, shall likewise have the right to proceed against the collateral(s), if there by any, securing the aforesaid loan. 
    It is understood that the acceptance of partial or late payment shall not alter or vary the terms of original conditions of the note. I/ WE hereby waive the right to make application of payment under Article 1252 of the Civil Code of the Philippines. 
    In the event the ${companyName} finds or discovers and defects, fraud misrepresentations in obtaining the loan evidenced by this Note shall become due and demandable. 
    Should it become necessary to collect the Note through a lawyer, I/WE hereby to pay jointly and severally, twenty five percent (25%) of the total amount due on this note as attorneys fees which is no case shall be less than Php. 1,000.00 exclusively in all costs and judicial/ extra expenses. I/ WE expressly agree that all legal action out of this Note may be brought in or submitted to the jurisdiction of the Proper Court either in Butuan City and/ or the Province, Municipality or where the payee holds office of any of its business is located at the sole option of the payee.
    `;

    drawFormattedText(
      longText,
      37, // Starting x-coordinate
      628, // Starting y-coordinate
      538, // Max width for wrapping
      12, // Line height
      helveticaFont, // Default font
      helveticaFontBold, // Bold font
      9, // Font size
      firstPage, // The PDF page
      rgb(0, 0, 0) // Text color
    );
    

    // Save the modified PDF
    const pdfBytes = await pdfDoc.save();

    // Use FileSaver to download the PDF
    saveAs(new Blob([pdfBytes]), `Promisorry_Note_${applicantDetails.firstName.toUpperCase()}_${applicantDetails.lastName.toUpperCase()}_${loan.id}.pdf`);

  };

  return (
    <>
      <h3>Applicant Details</h3>
      <Table className="list-container" hover responsive>
        <tbody>
          <tr>
            <td><strong>Full Name</strong></td>
            <td>{`${applicantDetails.firstName} ${applicantDetails.middleName || ""} ${applicantDetails.lastName}`}</td>
          </tr>
          <tr>
            <td><strong>Address</strong></td>
            <td>{`${applicantDetails.street}, ${applicantDetails.barangay}, ${applicantDetails.municipality}, ${applicantDetails.province}`}</td>
          </tr>
        </tbody>
      </Table>

      <h3>Loan Processing Details</h3>
      {loanProcessingDetails.length > 0 ? (
        <Table className="list-container" hover responsive>
          <thead>
            <tr>
              <th>Type</th>
              <th>Amount Granted</th>
              <th>Date Granted</th>
              <th>Interest Rate</th>
              <th>Loan Term</th>
              <th>Payment Type</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {loanProcessingDetails.map((loan, index) => (
              <tr key={index}>
                <td>{loan.loanProcessingType}</td>
                <td><ToPeso amount={parseFloat(loan.amountGranted).toFixed(2)} /></td>
                <td>{format(new Date(loan.dateGranted), "MM/dd/yyyy")}</td>
                <td>{loan.interestRate}%</td>
                <td>{loan.loanTerm} months</td>
                <td>{loan.paymentType}</td>
                <td>
                  <Button variant="primary" onClick={() => generatePDF(loan)}>
                    Generate
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
      ) : (
        <p>No loan processing details found for this applicant.</p>
      )}
    </>
  );
}

export default DocumentPromissoryNote;
